"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { FormControlLabel, Switch } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { SkuFormData } from "@/components/screens/ProductFormPage/types";
import useDialog from "@/hooks/use-dialog";
import { IKitComponentInput } from "@/interfaces/kit.interface";
import { SkuFormMessages } from "@/messages/sku/sku-form.messages";

import AddKitComponentDialog from "./components/addKitComponentDialog";
import { KitTable } from "./components/kitTable";
import UpdateKitComponentDialog from "./components/updateKitComponentDialog";

type Props = {
  messages: SkuFormMessages;
  isEditingSku: boolean;
};

function KitComponents({ messages, isEditingSku }: Props) {
  const { values, setFieldValue, handleChange } = useFormikContext<SkuFormData>();

  const {
    isVisible: isVisibleAddKitComponentDialog,
    handleClose: closeAddKitComponentDialog,
    handleOpen: openAddKitComponentDialog,
  } = useDialog();

  const {
    isVisible: isVisibleUpdateKitComponentDialog,
    handleClose: closeUpdateKitComponentDialog,
    handleOpen: openUpdateKitComponentDialog,
    selected: selectedKitToUpdate,
  } = useDialog<IKitComponentInput>();

  const {
    isVisible: isVisibleRemoveKitComponentDialog,
    handleClose: closeRemoveKitComponentDialog,
    handleOpen: openRemoveKitComponentDialog,
    selected: selectedKitToRemove,
  } = useDialog<IKitComponentInput>();

  const hasKit = Array.isArray(values?.kitComponents) && values?.kitComponents.length > 0;
  const disableKitSwitch = hasKit;

  const onConfirmRemove = async (kit: IKitComponentInput) => {
    const updatedKits = values.kitComponents.filter((k) => k.componentSkuId !== kit.componentSkuId);
    setFieldValue("kitComponents", updatedKits);

    if (isEditingSku) {
      const updatedKitsToDelete = [...values.kitsToDelete, kit.id];
      setFieldValue("kitsToDelete", updatedKitsToDelete);
    }

    closeRemoveKitComponentDialog();
  };

  return (
    <MDBox p={3}>
      <MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems={"center"}>
          <MDTypography variant="h5" fontWeight="bold">
            {messages.titleCardKit}
          </MDTypography>

          <FormControlLabel
            label={messages.kitLabel}
            control={
              <Switch
                name="kit"
                checked={!!values.kit}
                onChange={handleChange}
                disabled={disableKitSwitch}
              />
            }
          />
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            {values.kit && (
              <>
                <Grid display={"flex"} justifyContent={"flex-end"} mt={2} size={12}>
                  <MDButton
                    endIcon={<AddCircleOutlineIcon />}
                    variant="outlined"
                    color="dark"
                    onClick={openAddKitComponentDialog}
                  >
                    {messages.createButton}
                  </MDButton>
                </Grid>

                {values.kitComponents.length > 0 && (
                  <KitTable
                    sku={values}
                    messages={messages}
                    onEdit={openUpdateKitComponentDialog}
                    onRemove={openRemoveKitComponentDialog}
                  />
                )}
              </>
            )}

            <AddKitComponentDialog
              isVisible={isVisibleAddKitComponentDialog}
              onClose={closeAddKitComponentDialog}
              messages={messages}
              parentSKU={values}
              isEditing={isEditingSku}
            />

            {!!selectedKitToUpdate && (
              <UpdateKitComponentDialog
                isVisible={isVisibleUpdateKitComponentDialog}
                onClose={closeUpdateKitComponentDialog}
                messages={messages}
                kit={selectedKitToUpdate}
              />
            )}

            {selectedKitToRemove && (
              <ConfirmationDialog
                isOpen={isVisibleRemoveKitComponentDialog}
                onClose={closeRemoveKitComponentDialog}
                onConfirm={() => onConfirmRemove(selectedKitToRemove)}
                titleText={messages.deleteKitDialogTitle}
                descriptionText={messages.deleteKitDialogContent}
                cancelText={messages.deleteKitDialogCancel}
                confirmText={messages.deleteKitDialogConfirm}
                isLoading={false}
              />
            )}
          </Grid>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export default KitComponents;
